import { DashboardCard } from "@dashboard/components/Card";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useRef, useState } from "react";

import { HtmlPage } from "../../types";

export interface HtmlPagePreviewProps {
  page: Partial<HtmlPage>;
  onPreviewToggle?: (isOpen: boolean) => void;
}

export const HtmlPagePreview: React.FC<HtmlPagePreviewProps> = ({ page, onPreviewToggle }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(true);

  const renderPreview = () => {
    if (!iframeRef.current) return;

    const doc = iframeRef.current.contentDocument;

    if (!doc) return;

    // Define CSS variables that might be missing
    const defaultCSS = `
      :root {
        --white: #ffffff;
        --button-bg: rgba(255, 255, 255, 0.08);
        --button-hover: rgba(255, 255, 255, 0.12);
        --radius: 12px;
      }
    `;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Page Preview - ${page.name || 'Untitled'}</title>
          <style>
            ${defaultCSS}
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              margin: 0;
              padding: 20px;
              background: #ffffff;
              line-height: 1.6;
              color: #374151;
              min-height: calc(100vh - 40px);
            }
            
            /* Reset and normalize styles */
            * {
              box-sizing: border-box;
            }
            
            /* Smooth animations */
            * {
              transition: all 0.2s ease-in-out;
            }
            
            /* Error styling */
            .page-error {
              background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
              color: #dc2626;
              padding: 12px 16px;
              margin: 16px 0;
              border-radius: 8px;
              border-left: 4px solid #dc2626;
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
              font-size: 14px;
            }
            
            ${page.css || ''}
          </style>
        </head>
        <body>
          ${page.html || '<div style="text-align: center; padding: 40px; color: #6b7280;"><div style="font-size: 48px; margin-bottom: 16px;">📝</div><p style="font-size: 18px; margin: 0;">Add HTML content to see preview</p></div>'}
          <script>
            (function() {
              try {
                ${page.javascript || ''}
              } catch (error) {
                console.error('Page JavaScript Error:', error);
                const errorDiv = document.createElement('div');
                errorDiv.className = 'page-error';
                errorDiv.innerHTML = '<strong>JavaScript Error:</strong> ' + error.message;
                document.body.appendChild(errorDiv);
              }
            })();
          </script>
        </body>
      </html>
    `;

    doc.open();
    doc.write(html);
    doc.close();
  };

  useEffect(() => {
    if (isPreviewOpen) {
      // Small delay to ensure iframe is ready
      const timeoutId = setTimeout(() => {
        renderPreview();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isPreviewOpen, page.html, page.css, page.javascript]);

  useEffect(() => {
    onPreviewToggle?.(isPreviewOpen);
  }, [isPreviewOpen, onPreviewToggle]);

  return (
    <DashboardCard>
      <DashboardCard.Header>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Text size={3} fontWeight="medium">
              Live Preview
            </Text>
            {page.name && (
              <Text size={2} color="default2">
                {page.name}
              </Text>
            )}
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant={isPreviewOpen ? "secondary" : "primary"}
              size="small"
              onClick={() => {
                const newState = !isPreviewOpen;

                setIsPreviewOpen(newState);

                if (newState) {
                  setTimeout(() => renderPreview(), 150);
                }
              }}
            >
              {isPreviewOpen ? "Hide Preview" : "Show Preview"}
            </Button>
            {isPreviewOpen && (
              <Button
                variant="tertiary"
                size="small"
                onClick={() => renderPreview()}
              >
                Refresh
              </Button>
            )}
          </Box>
        </Box>
      </DashboardCard.Header>
      
      {isPreviewOpen && (
        <DashboardCard.Content>
          {!page.html ? (
            <Box 
              padding={8} 
              textAlign="center"
              style={{
                backgroundColor: "#f9fafb",
                borderRadius: "8px",
                border: "1px dashed #d1d5db"
              }}
            >
              <Text size={4} marginBottom={2}>📝</Text>
              <Text size={3} fontWeight="medium" marginBottom={1}>
                No content to preview
              </Text>
              <Text color="default2" size={2}>
                Add HTML, CSS, and JavaScript to see your page come to life
              </Text>
            </Box>
          ) : (
            <Box>
              <Box 
                marginBottom={3}
                padding={2}
                style={{
                  backgroundColor: "#f0f9ff",
                  borderRadius: "6px",
                  border: "1px solid #0ea5e9"
                }}
              >
                <Text size={2} color="info1">
                  Live preview updates automatically as you type
                </Text>
              </Box>
              <Box
                style={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
                }}
              >
                <iframe
                  ref={iframeRef}
                  style={{
                    width: "100%",
                    height: "900px",
                    border: "none",
                    backgroundColor: "#ffffff",
                    display: "block"
                  }}
                  sandbox="allow-scripts allow-same-origin"
                  title="Page Preview"
                />
              </Box>
            </Box>
          )}
        </DashboardCard.Content>
      )}
    </DashboardCard>
  );
};